import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.tsx';
import './styles/fonts.css';
import './styles/globals.css';
import { installAudioUnlock } from './audio/index.ts';
import { initInstallPrompt, isStandalone } from './pwa/install.ts';
import { useGameStore } from './store/game-store.ts';

installAudioUnlock();
initInstallPrompt();
// Home-screen (standalone) app: the page is pinned, only inner containers scroll
if (isStandalone()) document.documentElement.classList.add('standalone');

// Virtual keyboard: a focused field must not end up underneath it (the layout viewport shrinks, interactive-widget=resizes-content)
document.addEventListener('focusin', (e) => {
  const t = e.target;
  if (!(t instanceof HTMLElement) || !t.matches('input:not([type=checkbox]):not([type=radio]), textarea, select')) return;
  if (!window.matchMedia('(pointer: coarse)').matches) return;
  window.setTimeout(() => { if (document.activeElement === t) t.scrollIntoView({ block: 'center', behavior: 'smooth' }); }, 300);
});

// Home-screen shortcuts (manifest.webmanifest) open /?tab=fleets etc.: pick the start tab, then clean the address bar
{
  const q = new URLSearchParams(window.location.search);
  const tab = q.get('tab');
  if (tab && ['galaxy', 'empire', 'research', 'fleets', 'diplomacy', 'trade', 'community', 'account'].includes(tab)) {
    useGameStore.setState({ activeTab: tab as ReturnType<typeof useGameStore.getState>['activeTab'] });
  }
  if (tab || q.has('source')) window.history.replaceState(null, '', window.location.pathname);
}

// M11: dev-only visual labs (synthetic data, no API); the import.meta.env.DEV guard drops them from production builds
const lab = import.meta.env.DEV ? new URLSearchParams(window.location.search).get('lab') : null;
const Lab = lab === 'map' ? React.lazy(() => import('./lab/MapLab.tsx'))
  : lab === 'battle' ? React.lazy(() => import('./lab/BattleLab.tsx'))
  : lab === 'ui' ? React.lazy(() => import('./lab/UiLab.tsx'))
  : null;

// PWA: the worker never touches /api or the WebSocket (public/sw.js). Production builds only, so the
// Vite dev server and the E2E stacks never serve cached files.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => { navigator.serviceWorker.register('/sw.js').catch(() => undefined); });
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {Lab ? <React.Suspense fallback={null}><Lab /></React.Suspense> : <App />}
  </React.StrictMode>,
);
